/**
 * DynamicFormRenderer - JSON Schema-driven Form Component
 * 
 * STABILITY NOTES (Root-level stabilization):
 * - All mapped elements have stable, unique keys from schema IDs
 * - No {condition && element} patterns - use ternary with null instead
 * - No anonymous Fragments <> in conditionals - use keyed divs
 * - Consistent wrapper structure to prevent DOM reconciliation issues
 */
import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft,
  Save,
  Printer,
  Loader2,
  CheckCircle2,
  User,
  Building2,
  Briefcase,
  Shield,
  FileText,
  AlertCircle
} from "lucide-react";
import api from "../api";

// Icon mapping
const ICON_MAP = {
  user: User,
  building: Building2,
  briefcase: Briefcase,
  shield: Shield,
  "file-text": FileText,
  "check-circle": CheckCircle2
};

/**
 * Infer branding context from practice type.
 */
function inferBrandingFromPracticeType(practiceType) {
  if (!practiceType) return "studio";
  const normalized = practiceType.toLowerCase();
  if (normalized.includes("agenzia") || normalized.includes("gestionale agenzia") || normalized === "ga") {
    return "agenzia";
  }
  return "studio";
}

/**
 * Stable Error Message Component - prevents DOM instability
 */
function ErrorMessage({ error }) {
  return (
    <div className="min-h-[20px]">
      {error ? (
        <p className="text-xs text-red-500 mt-1">{error}</p>
      ) : null}
    </div>
  );
}

/**
 * Main DynamicFormRenderer Component
 */
export default function DynamicFormRenderer({
  template,
  editingForm = null,
  clients = [],
  operators = [],
  cases = [],
  currentOperator = null,
  onBack,
  onSaved
}) {
  // STABILITY FIX: Track mount status to prevent setState after unmount
  const isMountedRef = React.useRef(true);
  const saveTimeoutRef = React.useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({});
  const [brandingContext, setBrandingContext] = useState(template?.settings?.defaultBranding || "studio");
  const [brandingLocked, setBrandingLocked] = useState(false);
  const [linkedCaseId, setLinkedCaseId] = useState("");
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Initialize form data from template defaults
  useEffect(() => {
    if (!template) return;

    const initialData = {};
    
    // Extract defaults from schema
    for (const section of template.sections || []) {
      if (section.type === "static_text") continue;
      
      for (const field of section.fields || []) {
        const fieldId = field.id;
        const fieldType = field.type;
        let defaultValue = field.defaultValue;

        // Handle special defaults
        if (defaultValue === "today") {
          defaultValue = new Date().toISOString().split("T")[0];
        }

        // Set default by type
        if (defaultValue !== undefined) {
          initialData[fieldId] = defaultValue;
        } else if (fieldType === "consent_checkbox" || fieldType === "checkbox") {
          initialData[fieldId] = false;
        } else if (fieldType === "number") {
          initialData[fieldId] = 0;
        } else {
          initialData[fieldId] = "";
        }

        // Auto-fill from current operator
        if (field.autoFill?.fromCurrentUser && currentOperator) {
          initialData[fieldId] = currentOperator.fullName || "";
          initialData[`${fieldId}Id`] = currentOperator.id || "";
        }
      }
    }

    setFormData(initialData);
  }, [template, currentOperator]);

  // Load editing form data
  useEffect(() => {
    if (!editingForm) return;

    const savedData = editingForm.formData || {};
    setFormData(prev => ({ ...prev, ...savedData }));
    setBrandingContext(editingForm.brandingContext || "studio");
    setLinkedCaseId(editingForm.caseId || "");
    
    if (editingForm.caseId) {
      setBrandingLocked(true);
    }
  }, [editingForm]);

  // Field update handler
  const updateField = useCallback((fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) {
      setErrors(prev => ({ ...prev, [fieldId]: null }));
    }
    setSaveSuccess(false);
  }, [errors]);

  // Client selection handler
  const handleClientSelect = useCallback((fieldId, clientName) => {
    const client = clients.find(c => c.fullName === clientName);
    
    if (client) {
      setFormData(prev => ({
        ...prev,
        [fieldId]: client.fullName,
        [`${fieldId}Id`]: client.id,
        clientId: client.id,
        clientTaxCode: client.taxCode || prev.clientTaxCode || "",
        clientAddress: client.address || prev.clientAddress || ""
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [fieldId]: clientName,
        [`${fieldId}Id`]: ""
      }));
    }
    setSaveSuccess(false);
  }, [clients]);

  // Practice link handler
  const handleCaseSelect = useCallback((caseId) => {
    if (!caseId) {
      setLinkedCaseId("");
      setBrandingLocked(false);
      return;
    }

    const selectedCase = cases.find(c => c.id === caseId);
    if (selectedCase) {
      const inferredBranding = inferBrandingFromPracticeType(selectedCase.practiceType);
      const linkedClient = clients.find(c => c.id === selectedCase.clientId);

      setFormData(prev => ({
        ...prev,
        clientId: selectedCase.clientId || prev.clientId,
        clientName: linkedClient?.fullName || prev.clientName,
        clientTaxCode: linkedClient?.taxCode || prev.clientTaxCode,
        clientAddress: linkedClient?.address || prev.clientAddress
      }));

      setLinkedCaseId(caseId);
      setBrandingContext(inferredBranding);
      setBrandingLocked(true);
    }
    setSaveSuccess(false);
  }, [cases, clients]);

  // Validation
  const validateForm = useCallback(() => {
    const newErrors = {};

    for (const section of template?.sections || []) {
      if (section.type === "static_text") continue;

      for (const field of section.fields || []) {
        const fieldId = field.id;
        const isRequired = field.required;
        const value = formData[fieldId];

        if (isRequired) {
          if (field.type === "consent_checkbox" && !value) {
            newErrors[fieldId] = field.requiredMessage || "Questo consenso è obbligatorio";
          } else if (!value || (typeof value === "string" && !value.trim())) {
            newErrors[fieldId] = "Campo obbligatorio";
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [template, formData]);

  // Save handler
  const handleSave = useCallback(async (andPrint = false) => {
    if (!validateForm()) {
      // Scroll to first error field for better UX
      const firstErrorField = document.querySelector('[data-error="true"]');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      alert("Compila tutti i campi obbligatori prima di salvare.");
      return;
    }

    try {
      setIsSaving(true);

      const linkedCase = linkedCaseId ? cases.find(c => c.id === linkedCaseId) : null;

      const saveData = {
        formType: template.id,
        formName: `${template.name} - ${formData.clientName || "Cliente"}`,
        clientId: formData.clientId || null,
        clientName: formData.clientName || "",
        caseId: linkedCaseId || null,
        caseNumber: linkedCase?.caseNumber || null,
        operatorId: formData.operatorId || currentOperator?.id || null,
        operatorName: formData.operator || currentOperator?.fullName || "",
        brandingContext: brandingContext,
        templateVersion: template.version,
        formData: { ...formData, linkedCaseId }
      };

      let savedForm;
      if (editingForm) {
        savedForm = await api.updateForm(editingForm.id, saveData);
      } else {
        savedForm = await api.createForm(saveData);
      }

      // STABILITY FIX: Check if still mounted before state updates
      if (!isMountedRef.current) return;
      
      setSaveSuccess(true);

      if (andPrint && savedForm?.id) {
        await api.downloadFormPDF(savedForm.id);
      }

      // Clear timeout ref and schedule navigation with mount check
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          onSaved();
        }
        saveTimeoutRef.current = null;
      }, 1000);

    } catch (error) {
      if (isMountedRef.current) {
        alert(error.message || "Errore durante il salvataggio");
      }
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [validateForm, template, formData, linkedCaseId, brandingContext, editingForm, cases, currentOperator, onSaved]);

  // Early return if no template
  if (!template) {
    return (
      <Card className="shadow-md">
        <CardContent className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
          <p className="text-slate-600">Template non trovato</p>
          <Button variant="outline" onClick={onBack} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Indietro
          </Button>
        </CardContent>
      </Card>
    );
  }

  const sections = template.sections || [];
  const signaturesEnabled = template.signatures?.enabled || false;
  const signatureItems = template.signatures?.items || [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="dynamic-form-back">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Indietro
        </Button>
        <div>
          <h2 className="text-xl font-bold text-slate-800">{template.name}</h2>
          <p className="text-sm text-slate-500">{template.description}</p>
        </div>
      </div>

      {/* Form Card */}
      <Card className="shadow-md">
        <CardContent className="p-6 space-y-6">
          {/* Render Sections - STABLE: Each section in its own keyed container */}
          {sections.map((section, sectionIndex) => (
            <div key={`section-wrapper-${section.id || sectionIndex}`} className="section-container">
              {/* Separator - always render container, conditionally show content */}
              <div className={sectionIndex > 0 ? "pb-6" : "hidden"}>
                {sectionIndex > 0 ? <Separator /> : null}
              </div>
              <DynamicSection
                key={`section-${section.id || sectionIndex}`}
                section={section}
                formData={formData}
                errors={errors}
                clients={clients}
                operators={operators}
                cases={cases}
                linkedCaseId={linkedCaseId}
                brandingContext={brandingContext}
                brandingLocked={brandingLocked}
                onFieldChange={updateField}
                onClientSelect={handleClientSelect}
                onCaseSelect={handleCaseSelect}
                onBrandingChange={setBrandingContext}
              />
            </div>
          ))}

          {/* Signatures Preview - STABLE: Always render container */}
          <div className={signaturesEnabled ? "signatures-section" : "hidden"}>
            {signaturesEnabled ? (
              <div key="signatures-content">
                <Separator className="my-6" />
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h3 className="text-sm font-semibold text-slate-700 mb-4">Area Firme (nel PDF)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {signatureItems.map((sig, sigIndex) => (
                      <div key={`sig-${sig.id || sigIndex}`}>
                        <p className="text-sm text-slate-600 mb-2">{sig.label}</p>
                        <div className="border-b-2 border-slate-300 h-12"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* Actions - STABLE: Fixed structure with CSS visibility */}
      <div className="flex justify-end gap-3">
        <div className="flex items-center gap-2 mr-auto min-h-[28px]">
          <div className={`flex items-center gap-2 text-green-600 ${saveSuccess ? '' : 'hidden'}`}>
            <CheckCircle2 className="h-5 w-5" />
            <span>Modulo salvato con successo!</span>
          </div>
        </div>
        <Button variant="outline" onClick={onBack}>Annulla</Button>
        <Button 
          variant="outline" 
          onClick={() => handleSave(false)} 
          disabled={isSaving}
          data-testid="dynamic-form-save"
        >
          <Save className={`h-4 w-4 mr-2 ${isSaving ? 'hidden' : ''}`} />
          <Loader2 className={`h-4 w-4 mr-2 animate-spin ${isSaving ? '' : 'hidden'}`} />
          Salva
        </Button>
        <Button 
          onClick={() => handleSave(true)} 
          disabled={isSaving} 
          className="bg-teal-600 hover:bg-teal-700"
          data-testid="dynamic-form-save-print"
        >
          <Printer className={`h-4 w-4 mr-2 ${isSaving ? 'hidden' : ''}`} />
          <Loader2 className={`h-4 w-4 mr-2 animate-spin ${isSaving ? '' : 'hidden'}`} />
          Salva e Scarica PDF
        </Button>
      </div>
    </div>
  );
}

/**
 * DynamicSection - Renders a single section from the schema
 * STABILITY: Uses consistent wrapper structure, no conditional siblings
 */
function DynamicSection({
  section,
  formData,
  errors,
  clients,
  operators,
  cases,
  linkedCaseId,
  brandingContext,
  brandingLocked,
  onFieldChange,
  onClientSelect,
  onCaseSelect,
  onBrandingChange
}) {
  const IconComponent = ICON_MAP[section.icon] || FileText;
  const sectionId = section.id || "unknown";
  const fields = section.fields || [];
  const hasPracticeLink = section.includesPracticeLink && cases && cases.length > 0;
  const hasBrandingSelector = section.includesBrandingSelector;

  // Static text section (informativa, etc.)
  if (section.type === "static_text") {
    return (
      <div key={`static-${sectionId}`}>
        <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
          <IconComponent className="h-4 w-4" />
          {section.title}
        </h3>
        <div className={`bg-slate-50 border border-slate-200 rounded-lg p-4 ${section.style === "scrollable" ? "max-h-64 overflow-y-auto" : ""}`}>
          <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
            {section.content}
          </pre>
        </div>
      </div>
    );
  }

  // Regular section with fields
  const columns = section.columns || 1;
  const gridClass = columns === 2 ? "grid grid-cols-1 md:grid-cols-2 gap-4" : "space-y-4";

  return (
    <div key={`section-content-${sectionId}`}>
      {/* Section Header */}
      <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
        <IconComponent className="h-4 w-4" />
        {section.title}
      </h3>
      
      {/* Fields Grid */}
      <div className={gridClass}>
        {fields.map((field, fieldIndex) => (
          <DynamicField
            key={`field-${field.id || fieldIndex}`}
            field={field}
            value={formData[field.id]}
            error={errors[field.id]}
            clients={clients}
            operators={operators}
            onFieldChange={onFieldChange}
            onClientSelect={onClientSelect}
          />
        ))}
      </div>

      {/* Practice Link - STABLE: Always render container */}
      <div className={hasPracticeLink ? "mt-4" : "hidden"}>
        {hasPracticeLink ? (
          <div key="practice-link">
            <Label>Collega a Pratica Esistente (opzionale)</Label>
            <Select value={linkedCaseId || "none"} onValueChange={(v) => onCaseSelect(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="dynamic-link-case-select">
                <SelectValue placeholder="Seleziona pratica..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Nessuna pratica collegata —</SelectItem>
                {cases.map((c) => (
                  <SelectItem key={`case-${c.id}`} value={c.id}>
                    {c.caseNumber} - {c.subject?.substring(0, 40) || "N/D"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="min-h-[20px]">
              {linkedCaseId ? (
                <p className="text-xs text-teal-600 mt-1">
                  Il branding verrà impostato automaticamente in base al tipo pratica
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {/* Branding Selector - STABLE: Always render container */}
      <div className={hasBrandingSelector ? "mt-4" : "hidden"}>
        {hasBrandingSelector ? (
          <div key="branding-selector">
            <Label>Intestazione Documento (Branding)</Label>
            <div className="min-h-[24px]">
              {brandingLocked ? (
                <p className="text-xs text-amber-600 mb-2">
                  Branding determinato dal tipo pratica collegata
                </p>
              ) : (
                <span className="invisible text-xs">placeholder</span>
              )}
            </div>
            <div className="flex gap-4 mt-2">
              <label className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${brandingLocked ? "cursor-not-allowed opacity-70" : "cursor-pointer"} ${brandingContext === "studio" ? "border-teal-500 bg-teal-50" : "border-slate-200 hover:border-slate-300"}`}>
                <input
                  type="radio"
                  name={`branding-${sectionId}`}
                  value="studio"
                  checked={brandingContext === "studio"}
                  onChange={(e) => !brandingLocked && onBrandingChange(e.target.value)}
                  disabled={brandingLocked}
                  className="sr-only"
                />
                <Building2 className={`h-5 w-5 ${brandingContext === "studio" ? "text-teal-600" : "text-slate-400"}`} />
                <div>
                  <p className={`font-medium ${brandingContext === "studio" ? "text-teal-700" : "text-slate-600"}`}>Gestione Studio</p>
                  <p className="text-xs text-slate-500">Logo e intestazione Studio</p>
                </div>
              </label>
              <label className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${brandingLocked ? "cursor-not-allowed opacity-70" : "cursor-pointer"} ${brandingContext === "agenzia" ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
                <input
                  type="radio"
                  name={`branding-${sectionId}`}
                  value="agenzia"
                  checked={brandingContext === "agenzia"}
                  onChange={(e) => !brandingLocked && onBrandingChange(e.target.value)}
                  disabled={brandingLocked}
                  className="sr-only"
                />
                <Briefcase className={`h-5 w-5 ${brandingContext === "agenzia" ? "text-orange-600" : "text-slate-400"}`} />
                <div>
                  <p className={`font-medium ${brandingContext === "agenzia" ? "text-orange-700" : "text-slate-600"}`}>Gestione Agenzia</p>
                  <p className="text-xs text-slate-500">Logo Prisma Servizi</p>
                </div>
              </label>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * DynamicField - Renders a single field based on type
 * STABILITY: Each field type returns a consistent wrapper structure
 */
function DynamicField({
  field,
  value,
  error,
  clients,
  operators,
  onFieldChange,
  onClientSelect
}) {
  const fieldId = field.id;
  const isRequired = field.required;
  const colSpan = field.fullWidth ? "md:col-span-2" : "";

  // Text input
  if (field.type === "text") {
    return (
      <div className={colSpan} key={`field-text-${fieldId}`}>
        <Label>{field.label} {isRequired ? <span className="text-red-500">*</span> : null}</Label>
        <Input
          value={value || ""}
          onChange={(e) => onFieldChange(fieldId, e.target.value)}
          placeholder={field.placeholder}
          className={error ? "border-red-500" : ""}
          data-testid={`dynamic-field-${fieldId}`}
        />
        <ErrorMessage error={error} />
      </div>
    );
  }

  // Textarea
  if (field.type === "textarea") {
    return (
      <div className={colSpan} key={`field-textarea-${fieldId}`}>
        <Label>{field.label} {isRequired ? <span className="text-red-500">*</span> : null}</Label>
        <Textarea
          value={value || ""}
          onChange={(e) => onFieldChange(fieldId, e.target.value)}
          placeholder={field.placeholder}
          rows={field.rows || 4}
          className={error ? "border-red-500" : ""}
          data-testid={`dynamic-field-${fieldId}`}
        />
        <ErrorMessage error={error} />
      </div>
    );
  }

  // Date input
  if (field.type === "date") {
    return (
      <div className={colSpan} key={`field-date-${fieldId}`}>
        <Label>{field.label} {isRequired ? <span className="text-red-500">*</span> : null}</Label>
        <Input
          type="date"
          value={value || ""}
          onChange={(e) => onFieldChange(fieldId, e.target.value)}
          className={error ? "border-red-500" : ""}
          data-testid={`dynamic-field-${fieldId}`}
        />
        <ErrorMessage error={error} />
      </div>
    );
  }

  // Number input
  if (field.type === "number") {
    return (
      <div className={colSpan} key={`field-number-${fieldId}`}>
        <Label>{field.label} {isRequired ? <span className="text-red-500">*</span> : null}</Label>
        <Input
          type="number"
          value={value || 0}
          onChange={(e) => onFieldChange(fieldId, parseFloat(e.target.value) || 0)}
          placeholder={field.placeholder}
          className={error ? "border-red-500" : ""}
          data-testid={`dynamic-field-${fieldId}`}
        />
        <ErrorMessage error={error} />
      </div>
    );
  }

  // Select dropdown
  if (field.type === "select") {
    const options = field.options || [];
    return (
      <div className={colSpan} key={`field-select-${fieldId}`}>
        <Label>{field.label} {isRequired ? <span className="text-red-500">*</span> : null}</Label>
        <Select value={value || ""} onValueChange={(v) => onFieldChange(fieldId, v)}>
          <SelectTrigger className={error ? "border-red-500" : ""} data-testid={`dynamic-field-${fieldId}`}>
            <SelectValue placeholder={field.placeholder || "Seleziona..."} />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt, optIndex) => (
              <SelectItem key={`opt-${fieldId}-${opt.value || opt || optIndex}`} value={opt.value || opt}>
                {opt.label || opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ErrorMessage error={error} />
      </div>
    );
  }

  // Client select (with autocomplete)
  if (field.type === "client_select") {
    return (
      <div className={colSpan} key={`field-client-${fieldId}`}>
        <Label>{field.label} {isRequired ? <span className="text-red-500">*</span> : null}</Label>
        <Input
          value={value || ""}
          onChange={(e) => onClientSelect(fieldId, e.target.value)}
          placeholder={field.placeholder}
          list={`client-list-${fieldId}`}
          className={error ? "border-red-500" : ""}
          data-testid={`dynamic-field-${fieldId}`}
        />
        <datalist id={`client-list-${fieldId}`}>
          {clients.map(c => <option key={`client-opt-${c.id}`} value={c.fullName} />)}
        </datalist>
        <ErrorMessage error={error} />
      </div>
    );
  }

  // Operator select (with autocomplete)
  if (field.type === "operator_select") {
    return (
      <div className={colSpan} key={`field-operator-${fieldId}`}>
        <Label>{field.label} {isRequired ? <span className="text-red-500">*</span> : null}</Label>
        <Input
          value={value || ""}
          onChange={(e) => onFieldChange(fieldId, e.target.value)}
          placeholder={field.placeholder}
          list={`operator-list-${fieldId}`}
          className={error ? "border-red-500" : ""}
          data-testid={`dynamic-field-${fieldId}`}
        />
        <datalist id={`operator-list-${fieldId}`}>
          {operators.filter(op => op.status !== "sospeso").map(o => <option key={`op-opt-${o.id}`} value={o.fullName} />)}
        </datalist>
        <ErrorMessage error={error} />
      </div>
    );
  }

  // Consent checkbox (card style)
  if (field.type === "consent_checkbox") {
    const isChecked = !!value;
    const styleRequired = field.style === "card_required";
    
    let borderClass = "border-slate-200";
    if (isChecked) {
      borderClass = styleRequired ? "border-green-500 bg-green-50" : "border-blue-500 bg-blue-50";
    } else if (error) {
      borderClass = "border-red-500 bg-red-50";
    }

    return (
      <div className="md:col-span-2" key={`field-consent-${fieldId}`}>
        <div className={`p-4 rounded-lg border-2 ${borderClass}`}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => onFieldChange(fieldId, e.target.checked)}
              className={`mt-1 h-5 w-5 rounded border-slate-300 ${styleRequired ? "text-green-600 focus:ring-green-500" : "text-blue-600 focus:ring-blue-500"}`}
              data-testid={`dynamic-field-${fieldId}`}
            />
            <div>
              <p className="font-medium text-slate-700">
                {field.label}
                {isRequired ? <span className="text-red-500 ml-1">*</span> : null}
                {field.optionalLabel ? <span className="text-slate-400 text-sm ml-2">({field.optionalLabel})</span> : null}
              </p>
              <div className="min-h-[20px]">
                {field.description ? (
                  <p className="text-sm text-slate-500 mt-1">{field.description}</p>
                ) : null}
              </div>
            </div>
          </label>
          <div className="min-h-[20px] ml-8">
            {error ? <p className="text-xs text-red-500 mt-2">{error}</p> : null}
          </div>
        </div>
      </div>
    );
  }

  // Checkbox (simple)
  if (field.type === "checkbox") {
    return (
      <div className={colSpan} key={`field-checkbox-${fieldId}`}>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onFieldChange(fieldId, e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            data-testid={`dynamic-field-${fieldId}`}
          />
          <span className="text-sm text-slate-700">{field.label}</span>
        </label>
        <ErrorMessage error={error} />
      </div>
    );
  }

  // Unknown field type - fallback to text
  return (
    <div className={colSpan} key={`field-unknown-${fieldId}`}>
      <Label>{field.label}</Label>
      <Input
        value={value || ""}
        onChange={(e) => onFieldChange(fieldId, e.target.value)}
        placeholder={field.placeholder}
        data-testid={`dynamic-field-${fieldId}`}
      />
    </div>
  );
}
